import type { PaymentMethodEnum } from '@/types/common';
import { getSessionIdFromUrlParams } from '@/utils/location';

/**
 * A 3DS challenge that was on screen when this page stopped running.
 *
 * Spanish SCA is app-based by design — CaixaBank and Santander Key push a
 * notification the shopper must confirm in the banking app — so the challenge
 * is answered out-of-band while this tab is in the background. Chrome freezes
 * backgrounded pages and discards them under memory pressure: production shows
 * 36 `pagehide` events during an Adyen challenge in Spain against 1 in France.
 *
 * A freeze is survivable, because the JS context resumes intact and the
 * order-state watchdog simply carries on. A discard is not: the watchdog dies
 * with the page, and the checkout that reloads has no idea a challenge was ever
 * pending. Everything needed to pick the challenge back up therefore has to
 * live outside the JS heap, which is what this stores.
 */
export interface PendingChallenge {
  /**
   * The whole finalize response the challenge was created from, not just its
   * action. The action alone is enough to re-mount the component, but the
   * response is what the checkout's success handler is given on the normal
   * path — it polls the order from it and renders the success screen from it.
   * Storing anything narrower would resume a challenge that could complete and
   * still leave the shopper on a bare checkout.
   *
   * It holds no secrets: `paymentProvider`, the Adyen result/status/action and
   * `orderId`. The action token is the only sensitive part and it has to be
   * stored regardless.
   */
  params: unknown;
  orderId: string;
  paymentMethodId: string;
  activeMethod?: PaymentMethodEnum;
  /**
   * The checkout session the challenge belongs to, taken from the URL so it is
   * still there after a reload.
   *
   * `sessionStorage` is scoped to the tab, not to a purchase, so a record left
   * behind by an abandoned challenge outlives the checkout that wrote it. The
   * shopper opening a second purchase in the same tab would otherwise resume
   * the first one: a challenge for the old order, mounted inside the new
   * session, polling and rendering success for a payment that is not the one
   * in front of them. Binding to the session is what makes a stale record
   * inert rather than dangerous.
   */
  sessionId: string;
  savedAt: number;
}

const PENDING_CHALLENGE_KEY = 'appcharge:adyen-3ds-pending';

/**
 * EMVCo gives each challenge interaction a 10-minute timeout at the ACS, so a
 * record older than that describes a challenge the issuer has already
 * abandoned. Re-mounting it would put a dead challenge in front of the shopper
 * instead of letting them start a clean payment.
 */
export const PENDING_CHALLENGE_TTL_MS = 10 * 60 * 1000;

/**
 * `sessionStorage` survives tab discard (Chrome restores it with the tab) but
 * is not always reachable: Safari private mode throws on write, and a
 * cross-origin iframe can have storage partitioned or blocked outright. The
 * checkout runs framed on publisher domains, so every access has to assume it
 * can fail, and failing must never disturb a live challenge.
 */
const readStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const savePendingChallenge = (entry: Omit<PendingChallenge, 'savedAt' | 'sessionId'>): void => {
  const storage = readStorage();
  if (!storage) return;

  // Stamped here rather than taken from the caller so no call site can forget
  // it and write a record that later resumes into the wrong session.
  const sessionId = getSessionIdFromUrlParams();
  // Without a session to bind to there is nothing to check on the way back, so
  // the record could only ever be resumed blind. Not writing is the safe half.
  if (!sessionId) return;

  try {
    storage.setItem(PENDING_CHALLENGE_KEY, JSON.stringify({ ...entry, sessionId, savedAt: Date.now() }));
  } catch {
    // Quota or a blocked partition. The challenge itself is unaffected; only
    // the ability to recover from a discard is lost.
  }
};

export const clearPendingChallenge = (): void => {
  const storage = readStorage();
  if (!storage) return;
  try {
    storage.removeItem(PENDING_CHALLENGE_KEY);
  } catch {
    // Nothing actionable — a stale record is bounded by the TTL above.
  }
};

/**
 * Returns the pending challenge only when it is still usable, and clears the
 * record otherwise. Reading is destructive-on-invalid by design: a malformed or
 * expired entry that survived would be retried on every subsequent boot.
 */
export const readPendingChallenge = (): PendingChallenge | null => {
  const storage = readStorage();
  if (!storage) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(PENDING_CHALLENGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingChallenge>;
    const isUsable =
      parsed &&
      typeof parsed.orderId === 'string' &&
      parsed.orderId.length > 0 &&
      typeof parsed.paymentMethodId === 'string' &&
      typeof parsed.savedAt === 'number' &&
      parsed.params !== undefined &&
      parsed.params !== null;

    if (!isUsable || Date.now() - (parsed.savedAt as number) > PENDING_CHALLENGE_TTL_MS) {
      clearPendingChallenge();
      return null;
    }

    // A record from a different purchase in this tab. Resuming it would mount
    // the old order's challenge inside the new session, so it is dropped —
    // and deleted, because it can never become valid again.
    const sessionId = getSessionIdFromUrlParams();
    if (!sessionId || parsed.sessionId !== sessionId) {
      clearPendingChallenge();
      return null;
    }

    return parsed as PendingChallenge;
  } catch {
    clearPendingChallenge();
    return null;
  }
};
